import { router, usePage } from "@inertiajs/react"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { org_revenue_path, org_revenue_products_path } from "@/lib/routes"
import type { Market } from "@/lib/useMarket"
import RevenueToolbar, { ToolbarGroup } from "./components/RevenueToolbar"
import CurrencyMenu from "./components/CurrencyMenu"
import OverviewChart from "./components/OverviewChart"
import MarketHeader from "./components/MarketHeader"
import ProductsBar from "./components/ProductsBar"
import RevenuePivotTable from "./components/RevenuePivotTable"
import { formatRange } from "./format"
import { hrefFor, toQuery } from "./query"
import { rateLine, rateLines } from "./rates"
import { nextSort } from "./sort"
import { GRAIN_LABEL, type Filters, type MarketSection, type OverviewPageProps, type PivotRow } from "./types"

export default function RevenueOverview({ filters, markets, currencies, display_currency, rate_date, rates }: OverviewPageProps) {
  const { org_markets } = usePage<{ org_markets: Market[] }>().props
  const navigate = (next: Filters, currency: string | null) => {
    router.get(org_revenue_path(), toQuery(next, null, currency), { preserveState: true, preserveScroll: true })
  }
  const update = (patch: Partial<Filters>) => navigate({ ...filters, ...patch }, display_currency)

  const products = filters.rows === "products"
  const autoProducts = markets[0]?.pivot.auto_products ?? filters.products.length === 0
  const onSort = (key: string) => update(nextSort({ sort: filters.sort, dir: filters.dir }, key))
  const chartTitle = `Revenue by ${GRAIN_LABEL[filters.grain]}`
  const chartSubtitle = ["All markets", formatRange(filters.from, filters.to), products ? "Listed products only" : null]
    .filter(Boolean)
    .join(" · ")

  // Channel rows drill into that market's page. The market's own row (a
  // market with no channels) opens the market page unscoped.
  const hrefForRow = (market: MarketSection) => (row: PivotRow) =>
    row.slug ? hrefFor(market.slug, filters, row.key === market.id ? null : row.slug) : null

  return (
    <AppLayout header={<PageTitle title="Revenue" subtitle="Sales revenue across every market" />}>
      <div className="flex flex-col gap-4">
        <RevenueToolbar filters={filters} onChange={update}>
          <ToolbarGroup label="Show in">
            <div className="flex h-9 items-center">
              <CurrencyMenu currencies={currencies} value={display_currency} onChange={(c) => navigate(filters, c)} />
            </div>
          </ToolbarGroup>
        </RevenueToolbar>

        {markets.length === 0 ? (
          <p className="text-sm text-muted-foreground">No markets yet. Add a country-level org node to get started.</p>
        ) : (
          <>
            <OverviewChart
              markets={markets}
              rows={filters.rows}
              displayCurrency={display_currency}
              rateDate={rate_date}
              rateLines={rateLines(display_currency, currencies, rates)}
              title={chartTitle}
              subtitle={chartSubtitle}
            />

            {products && (
              <ProductsBar
                autoProducts={autoProducts}
                picked={filters.products}
                searchUrl={org_revenue_products_path()}
                onAdd={(id) => update({ products: [...(autoProducts ? [] : filters.products), id] })}
                onReset={() => update({ products: [] })}
              />
            )}

            {markets.map((market) => (
              <RevenuePivotTable
                key={market.id}
                pivot={market.pivot}
                rows={filters.rows}
                currency={market.currency}
                displayCurrency={display_currency}
                rowsHeading={products ? "Product" : "Channel"}
                sort={filters.sort}
                dir={filters.dir}
                onSort={onSort}
                header={
                  <MarketHeader
                    market={market}
                    href={hrefFor(market.slug, filters, null)}
                    displayCurrency={display_currency}
                    rateLine={display_currency && market.currency ? rateLine(display_currency, market.currency, rates) : null}
                  />
                }
                hrefForRow={products ? undefined : hrefForRow(market)}
                productMarket={org_markets.find((m) => m.slug === market.slug) ?? null}
                onRemoveRow={
                  products && !autoProducts
                    ? (row) => update({ products: filters.products.filter((id) => id !== row.key) })
                    : undefined
                }
              />
            ))}
          </>
        )}
      </div>
    </AppLayout>
  )
}
